import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { http } from "../../lib/http";
import PageChrome from "../../components/shell/PageChrome";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { Card } from "../../components/ui/card";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "../../components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import { Plus, MoreVertical, Search, Users, MapPin, Clock, BookOpen, Calendar as CalendarIcon } from "lucide-react";
import { ClassEditModal } from "./ClassEditModal";
import { useAuth } from "../../contexts/AuthContext";
import { useOutletContext } from "react-router-dom";
import ConfirmDialog from "../../components/data/ConfirmDialog";

const LEVELS = ["pre_a1","a1","a1_plus","a2","a2_plus","b1","b1_plus","b2","b2_plus","c1","c1_plus","c2"];
const WD_SHORT = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];

export function currentAcademicYear(today) {
  const d = today ? new Date(today) : new Date();
  const y = d.getFullYear();
  return d.getMonth() >= 8 ? `${y}-${y + 1}` : `${y - 1}-${y}`;
}

function ScheduleLine({ schedule }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {(schedule || []).map((b, i) => (
        <span key={i} className="px-2 py-0.5 rounded-md bg-slate-100 text-slate-700 text-xs font-medium">
          {WD_SHORT[b.weekday]} {b.start_time}–{b.end_time}
        </span>
      ))}
    </div>
  );
}

function CapacityBar({ count, capacity }) {
  const pct = Math.min(100, Math.round((count / Math.max(1, capacity)) * 100));
  const tone = pct >= 90 ? "bg-[#C8102E]" : pct >= 70 ? "bg-amber-500" : "bg-emerald-500";
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs">
        <span className="text-slate-600">{count}/{capacity}</span>
        <span className="text-slate-500">{pct}%</span>
      </div>
      <div className="h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
        <div className={`h-full ${tone}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function ClassCard({ cls, rooms, teachers, canEdit, isAdmin, onView, onEdit, onDuplicate, onArchive, onDelete, onClickRegister }) {
  const room = rooms.find(r => r.id === cls.room_id);
  const teacher = teachers.find(t => t.user_id === cls.teacher_user_id);
  const ring = room?.color || "#0A1F44";
  const [reportOpen, setReportOpen] = React.useState(false);
  const generateClassReport = async (term) => {
    setReportOpen(false);
    try {
      const base = process.env.REACT_APP_BACKEND_URL || "";
      const token = localStorage.getItem("eata.access_token");
      const res = await fetch(`${base}/api/reportcards/class/${cls.id}?term=${term}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 409) {
        const d = await res.json();
        toast.error(`Cannot generate — ${(d.detail?.ids || []).length} unpublished grade(s). Publish first.`);
        return;
      }
      if (!res.ok) { toast.error("Generation failed"); return; }
      const blob = await res.blob();
      window.open(URL.createObjectURL(blob), "_blank");
    } catch { toast.error("Generation failed"); }
  };
  const generateStudentReportPrompt = async () => {
    setReportOpen(false);
    const roster = cls.enrolled_student_ids || [];
    if (!roster.length) { toast.error("No students enrolled"); return; }
    const sid = window.prompt("Student id (or press OK for first):", roster[0]);
    if (!sid) return;
    try {
      const base = process.env.REACT_APP_BACKEND_URL || "";
      const token = localStorage.getItem("eata.access_token");
      const res = await fetch(`${base}/api/reportcards/student/${sid}?term=T1`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = await res.blob();
      window.open(URL.createObjectURL(blob), "_blank");
    } catch { toast.error("Generation failed"); }
  };
  return (
    <Card className="p-5 bg-white border border-slate-200 hover:shadow-md transition-shadow space-y-4"
          data-testid={`class-card-${cls.id}`}>
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className="h-10 w-1 rounded-full shrink-0" style={{ background: ring }} />
          <div className="min-w-0 flex-1">
            <h3 className="font-serif text-lg leading-tight text-[#0A1F44] truncate" style={{ fontFamily: "'Segoe UI', 'Selawik', Tahoma, Geneva, Verdana, system-ui, sans-serif" }}>
              {cls.name}
            </h3>
            <div className="flex flex-wrap items-center gap-2 mt-1">
              <Badge variant="secondary" className="uppercase text-[10px] tracking-wider">{cls.level?.replace("_"," ")}</Badge>
              <Badge variant={cls.status === "active" ? "default" : "outline"}>{cls.status}</Badge>
            </div>
          </div>
        </div>
        {canEdit && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`class-menu-${cls.id}`}>
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onView(cls)} data-testid="class-action-view">View</DropdownMenuItem>
              <DropdownMenuItem onClick={() => onEdit(cls)} data-testid="class-action-edit">Edit</DropdownMenuItem>
              <DropdownMenuItem onClick={() => onDuplicate(cls)} data-testid="class-action-duplicate">Duplicate</DropdownMenuItem>
              <DropdownMenuItem onClick={() => onArchive(cls)} data-testid="class-action-archive" className="text-amber-700">Archive</DropdownMenuItem>
              {isAdmin && (
                <DropdownMenuItem onClick={() => onDelete(cls)} data-testid="class-action-delete" className="text-[#C8102E] font-medium">Delete</DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      <div className="space-y-2 text-sm text-slate-700">
        <div className="flex items-center gap-2"><Users className="h-3.5 w-3.5 text-slate-400"/>{teacher?.full_name || "—"}</div>
        <div className="flex items-center gap-2"><MapPin className="h-3.5 w-3.5 text-slate-400"/>{room?.name || "—"}</div>
        <div className="flex items-start gap-2"><Clock className="h-3.5 w-3.5 text-slate-400 mt-0.5"/><ScheduleLine schedule={cls.schedule}/></div>
        {cls.academic_year && (
          <div className="flex items-center gap-2 text-xs text-slate-500" data-testid={`class-academic-year-${cls.id}`}>
            <CalendarIcon className="h-3 w-3 text-slate-400"/>
            Academic year {cls.academic_year}
          </div>
        )}
      </div>

      <CapacityBar count={(cls.enrolled_student_ids || []).length} capacity={cls.capacity || 0} />

      <div className="flex items-center justify-between gap-2 pt-2 border-t border-slate-100">
        <Button variant="outline" size="sm" onClick={() => onView(cls)} data-testid={`class-view-${cls.id}`}>View</Button>
        {canEdit && (
          <Button variant="outline" size="sm" onClick={() => onEdit(cls)} data-testid={`class-edit-${cls.id}`}>Edit</Button>
        )}
        <Button variant="ghost" size="sm" asChild data-testid={`class-register-${cls.id}`}>
          <Link to={`/attendance?class_id=${cls.id}&date=${new Date().toISOString().slice(0,10)}`}>Register</Link>
        </Button>
        <div className="relative">
          <Button variant="ghost" size="sm" onClick={() => setReportOpen(v => !v)} data-testid={`class-report-${cls.id}`}>Report</Button>
          {reportOpen && (
            <div className="absolute right-0 bottom-full mb-2 w-56 bg-white border border-slate-200 rounded-lg shadow-lg p-2 z-10 space-y-1" data-testid={`class-report-popover-${cls.id}`}>
              <button onClick={generateStudentReportPrompt} className="w-full text-left text-xs p-2 rounded hover:bg-slate-50">Student report card (T1)</button>
              <button onClick={() => generateClassReport("T1")} className="w-full text-left text-xs p-2 rounded hover:bg-slate-50">Class report (T1)</button>
              <button onClick={() => setReportOpen(false)} className="w-full text-left text-xs p-2 rounded hover:bg-slate-50 text-slate-400">Cancel</button>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

export default function ClassesPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { permissions } = useOutletContext() || {};
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [students, setStudents] = useState([]);
  const [q, setQ] = useState("");
  const [level, setLevel] = useState("all");
  const [teacherId, setTeacherId] = useState("all");
  const [roomId, setRoomId] = useState("all");
  const [status, setStatus] = useState("active");
  const [academicYear, setAcademicYear] = useState(currentAcademicYear());
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);   // class pending delete
  const [conflictInfo, setConflictInfo] = useState(null);     // 409 payload for archive-instead flow

  const canCreate = !!permissions?.classes?.create;
  const canEdit = !!permissions?.classes?.edit;
  const isAdmin = user?.role === "admin";

  const fetchData = async () => {
    const params = new URLSearchParams();
    if (q) params.append("q", q);
    if (level !== "all") params.append("level", level);
    if (teacherId !== "all") params.append("teacher_id", teacherId);
    if (roomId !== "all") params.append("room_id", roomId);
    if (status !== "all") params.append("status", status);
    if (academicYear !== "all") params.append("academic_year", academicYear);
    const [c, r, u, s] = await Promise.all([
      http.get(`/classes?${params.toString()}`),
      http.get(`/rooms`),
      http.get(`/classes/teachers-in-scope`).catch(() => ({ data: { items: [] }})),
      http.get(`/students?limit=200&active=true`),
    ]);
    setItems(c.data.items || []);
    setRooms(r.data.items || []);
    setTeachers(u.data.items || []);
    setStudents(s.data.items || []);
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [q, level, teacherId, roomId, status, academicYear]);

  // Distinct academic_year list from current items (plus current year)
  const academicYearOptions = React.useMemo(() => {
    const set = new Set([currentAcademicYear()]);
    for (const c of items) if (c.academic_year) set.add(c.academic_year);
    return [...set].sort().reverse();
  }, [items]);

  const handleSaved = () => { setEditing(null); setCreating(false); fetchData(); };
  const handleDuplicate = async (c) => {
    try {
      await http.post(`/classes/${c.id}/duplicate`);
      toast.success(t("classes.duplicated"));
      fetchData();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const handleArchive = async (c) => {
    if (!window.confirm(t("classes.archiveConfirm", { name: c.name }))) return;
    try {
      await http.post(`/classes/${c.id}/archive`);
      toast.success(t("classes.archived"));
      fetchData();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const handleDelete = async () => {
    const c = deleteConfirm; setDeleteConfirm(null);
    if (!c) return;
    try {
      await http.delete(`/classes/${c.id}`);
      toast.success(t("classes.deleted", "Class deleted"));
      fetchData();
    } catch (e) {
      const det = e?.response?.data?.detail;
      if (e?.response?.status === 409 && det?.affected) {
        setConflictInfo({ cls: c, affected: det.affected });
      } else {
        toast.error(det?.message || det || "Error");
      }
    }
  };
  const archiveFromConflict = async () => {
    const c = conflictInfo?.cls; setConflictInfo(null);
    if (!c) return;
    try {
      await http.post(`/classes/${c.id}/archive`);
      toast.success(t("classes.archived"));
      fetchData();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  return (
    <PageChrome title={t("classes.title")} subtitle={t("classes.subtitle")}>
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="relative flex-1 min-w-[220px] max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input placeholder={t("classes.search")} value={q} onChange={(e) => setQ(e.target.value)}
                 className="pl-9 bg-white" data-testid="classes-search"/>
        </div>
        <Select value={level} onValueChange={setLevel}>
          <SelectTrigger className="w-[140px] bg-white" data-testid="classes-filter-level"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("common.all")} — {t("classes.level")}</SelectItem>
            {LEVELS.map(l => <SelectItem key={l} value={l}>{l.replace("_"," ").toUpperCase()}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={teacherId} onValueChange={setTeacherId}>
          <SelectTrigger className="w-[180px] bg-white" data-testid="classes-filter-teacher"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("common.all")} — {t("classes.teacher")}</SelectItem>
            {teachers.map(tc => <SelectItem key={tc.user_id} value={tc.user_id}>{tc.full_name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={academicYear} onValueChange={setAcademicYear}>
          <SelectTrigger className="w-[160px] bg-white" data-testid="classes-filter-academic-year"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("classes.allYears", "All years")}</SelectItem>
            {academicYearOptions.map(y => (
              <SelectItem key={y} value={y}>{t("classes.academicYearLabel", "AY")} {y}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={roomId} onValueChange={setRoomId}>
          <SelectTrigger className="w-[140px] bg-white" data-testid="classes-filter-room"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("common.all")} — {t("classes.room")}</SelectItem>
            {rooms.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-[140px] bg-white" data-testid="classes-filter-status"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("common.all")}</SelectItem>
            <SelectItem value="active">{t("common.active")}</SelectItem>
            <SelectItem value="draft">{t("classes.status_draft")}</SelectItem>
            <SelectItem value="archived">{t("classes.status_archived")}</SelectItem>
          </SelectContent>
        </Select>

        <div className="ml-auto flex items-center gap-2">
          <Button asChild variant="outline"><Link to="/calendar" data-testid="classes-goto-calendar">
            <BookOpen className="h-4 w-4 mr-2"/>{t("nav.calendar")}
          </Link></Button>
          {canCreate && (
            <Button onClick={() => setCreating(true)} className="bg-[#0A1F44] hover:bg-[#0A1F44]/90" data-testid="classes-add-button">
              <Plus className="h-4 w-4 mr-2"/>{t("classes.addClass")}
            </Button>
          )}
        </div>
      </div>

      {items.length === 0 ? (
        <Card className="p-12 text-center bg-white text-slate-500" data-testid="classes-empty">{t("classes.empty")}</Card>
      ) : (
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {items.map(c => (
            <ClassCard
              key={c.id} cls={c} rooms={rooms} teachers={teachers}
              canEdit={canEdit}
              isAdmin={isAdmin}
              onView={() => nav(`/calendar?class_id=${c.id}`)}
              onEdit={() => setEditing(c)}
              onDuplicate={handleDuplicate}
              onArchive={handleArchive}
              onDelete={() => setDeleteConfirm(c)}
            />
          ))}
        </div>
      )}

      {(creating || editing) && (
        <ClassEditModal
          open
          initial={editing}
          rooms={rooms}
          teachers={teachers}
          students={students}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={handleSaved}
          isAdmin={user?.role === "admin"}
        />
      )}

      <ConfirmDialog
        open={!!deleteConfirm}
        title={t("classes.deleteTitle", "Delete class permanently?")}
        message={t("classes.deleteBody", `This cannot be undone. "${deleteConfirm?.name || ""}" will be removed.`)}
        confirmText={t("classes.deleteConfirm", "Delete permanently")}
        danger={true}
        onCancel={() => setDeleteConfirm(null)}
        onConfirm={handleDelete}
        testid="confirm-delete-class"
      />

      <Dialog open={!!conflictInfo} onOpenChange={(o) => !o && setConflictInfo(null)}>
        <DialogContent data-testid="class-conflict-dialog">
          <DialogHeader>
            <DialogTitle>{t("classes.cannotDelete", "Cannot delete this class")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p>{t("classes.linkedRecords", `"${conflictInfo?.cls?.name || ""}" has linked records that must be preserved.`)}</p>
            <div className="border rounded p-3 bg-amber-50 grid grid-cols-2 gap-2">
              {Object.entries(conflictInfo?.affected || {}).map(([k, v]) => (
                <div key={k} className="flex justify-between text-xs"><span className="capitalize text-slate-700">{k.replace(/_/g, " ")}</span><strong>{v}</strong></div>
              ))}
            </div>
            <p className="text-xs text-slate-600">{t("classes.useArchiveInstead", "Archive instead — the class will be hidden but its history preserved.")}</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConflictInfo(null)}>{t("common.cancel", "Cancel")}</Button>
            <Button className="bg-amber-700 hover:bg-amber-800" onClick={archiveFromConflict} data-testid="conflict-archive-instead">
              {t("classes.archiveInstead", "Archive instead")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageChrome>
  );
}
